import { z } from "zod";
import { Decision, IsoDateTime, PlayerRef } from "../common.ts";
import { ReasonCodeSchema } from "../reasons.ts";
import { OperatorFilter, PeriodBound, PeriodV1 } from "./common.ts";

// Overview map (spec explorer-power-map, design D7; `explorer.read`). The browser never receives
// more than 10,000 points per request; without `location.precise.read` only precision-5 cells are
// returned at every zoom; with it, zoom 12 or more returns individual markers (audited).

export const MAX_MAP_POINTS = 10_000;

const Longitude = z.number().min(-180).max(180);
const Latitude = z.number().min(-90).max(90);

/** `minLng,minLat,maxLng,maxLat` in degrees (WGS 84). */
export const BoundingBoxParam = z
  .string()
  .max(120)
  .regex(/^-?\d{1,3}(\.\d+)?(,-?\d{1,3}(\.\d+)?){3}$/)
  .transform((text) => text.split(",").map(Number))
  .pipe(
    z.tuple([Longitude, Latitude, Longitude, Latitude]).refine(([w, s, e, n]) => w < e && s < n, {
      message: "bbox must be minLng,minLat,maxLng,maxLat with min < max",
    }),
  );

const mapFilters = {
  from: PeriodBound.optional(),
  to: PeriodBound.optional(),
  operator_id: OperatorFilter.optional(),
  decision: Decision.optional(),
  reason: ReasonCodeSchema.optional(),
  source: z.enum(["browser", "ip"]).optional(),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  state: z
    .string()
    .regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/)
    .optional(),
  /** Current player status (now, not at the time of each transaction). */
  player_status: z.enum(["none", "blocked"]).optional(),
};

export const MapClustersQueryV1 = z
  .object({
    ...mapFilters,
    bbox: BoundingBoxParam,
    zoom: z.coerce.number().int().min(0).max(22),
    /** Opened from a profile: served from the transactions of that player or device. */
    player_ref: PlayerRef.optional(),
    device_ref: z.uuid().optional(),
  })
  .strict()
  .refine((q) => q.player_ref === undefined || q.device_ref === undefined, {
    message: "player_ref and device_ref are mutually exclusive",
    path: ["device_ref"],
  })
  .meta({ id: "MapClustersQueryV1" });
export type MapClustersQueryV1 = z.infer<typeof MapClustersQueryV1>;

export const MapClusterV1 = z
  .object({
    /** Geohash prefix of the cluster (its precision follows the zoom). */
    geohash: z.string().regex(/^[0-9b-hjkmnp-z]{1,5}$/),
    /** Weighted centroid of the cells' centers. */
    latitude: Latitude,
    longitude: Longitude,
    count: z.int().positive(),
    denied: z.int().nonnegative(),
  })
  .strict()
  .meta({ id: "MapClusterV1" });

export const MapMarkerV1 = z
  .object({
    verification_id: z.uuid(),
    operator_id: z.uuid(),
    decision: Decision,
    latitude: Latitude,
    longitude: Longitude,
    accuracy_m: z.number().nonnegative().optional(),
    /** Coordinates past retention: positioned at the precision-5 cell center. */
    approximate_location: z.boolean(),
  })
  .strict()
  .meta({ id: "MapMarkerV1" });

export const MapClustersResponseV1 = z
  .object({
    period: PeriodV1,
    resolution: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("cells"), precision: z.int().min(2).max(5) }).strict(),
      z.object({ kind: z.literal("precise_markers") }).strict(),
    ]),
    clusters: z.array(MapClusterV1).max(MAX_MAP_POINTS),
    markers: z.array(MapMarkerV1).max(MAX_MAP_POINTS),
    /** "No position (IP country only)" panel: transactions without a position, per country. */
    no_position: z.array(z.object({ country: z.string(), count: z.int().positive() }).strict()),
    /** More than 10,000 points matched; zoom in or narrow the filters. */
    truncated: z.boolean(),
  })
  .strict()
  .meta({ id: "MapClustersResponseV1" });
export type MapClustersResponseV1 = z.infer<typeof MapClustersResponseV1>;

export const MAX_POLYGON_POINTS = 100;
export const MAX_AREA_ENTITIES = 100;

/** `POST /map/areas/entities`: users and transactions in a clicked cluster or a drawn area. */
export const AreaEntitiesRequestV1 = z
  .object({
    ...mapFilters,
    area: z.discriminatedUnion("kind", [
      z
        .object({ kind: z.literal("cell"), geohash: z.string().regex(/^[0-9b-hjkmnp-z]{1,5}$/) })
        .strict(),
      z
        .object({
          kind: z.literal("polygon"),
          /** `[longitude, latitude]` vertices of a rectangle or polygon (closed implicitly). */
          points: z
            .array(z.tuple([Longitude, Latitude]))
            .min(3)
            .max(MAX_POLYGON_POINTS),
        })
        .strict(),
    ]),
    limit: z.int().min(1).max(MAX_AREA_ENTITIES).default(50),
  })
  .strict()
  .meta({ id: "AreaEntitiesRequestV1" });
export type AreaEntitiesRequestV1 = z.infer<typeof AreaEntitiesRequestV1>;

export const AreaEntitiesResponseV1 = z
  .object({
    period: PeriodV1,
    users: z.array(
      z
        .object({
          operator_id: z.uuid(),
          player_ref: PlayerRef,
          transactions: z.int().positive(),
          denied: z.int().nonnegative(),
          last_seen_at: IsoDateTime,
        })
        .strict(),
    ),
    transactions: z.array(
      z
        .object({
          verification_id: z.uuid(),
          operator_id: z.uuid(),
          player_ref: PlayerRef,
          decision: Decision,
          occurred_at: IsoDateTime,
        })
        .strict(),
    ),
    truncated: z.boolean(),
  })
  .strict()
  .meta({ id: "AreaEntitiesResponseV1" });
export type AreaEntitiesResponseV1 = z.infer<typeof AreaEntitiesResponseV1>;
